// src/layouts/trafficmonitoring/mfd/index.js
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { Bubble } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

import { speedFlowsMFDData } from "./speedFlowsMFDData";

function MFD() {
  const { t } = useTranslation();
  const realtimeLabel = t("trafficMonitoring.mfd.realtimeValue");
  // keep initial data for first render (areas list, names, etc.)
  const [dataMFD, setDataMFD] = useState(null);
  const [loading, setLoading] = useState(true);

  const timePooling = 300000;

  // store Chart.js instances per area_id to update imperatively
  const chartRefs = useRef({});

  // Plugin that forces the "Realtime value" point to be drawn last (on top)
  const realtimeOnTop = useMemo(
    () => ({
      id: "realtimeOnTop",
      afterDatasetsDraw(chart) {
        const realtimeIdx = chart.data.datasets.findIndex(
          (d) => d.label === realtimeLabel
        );
        if (realtimeIdx < 0) return;

        const meta = chart.getDatasetMeta(realtimeIdx);
        const point = meta?.data?.[0];
        if (!point) return;

        // redraw that point after everyone else
        point.draw(chart.ctx);
      },
    }),
    [realtimeLabel]
  );

  const applyUpdateToCharts = useCallback((fresh) => {
    // Update each chart in place (no React re-render)
    (fresh?.areas || []).forEach((area) => {
      const chart = chartRefs.current[area.area_id];
      if (!chart) return;

      // Find datasets by label (keep your labels stable!)
      const dsRealtime = chart.data.datasets.find(
        (d) => d.label === t("trafficMonitoring.mfd.flowForArea", { areaId: area.area_id })
      );
      const dsTypical = chart.data.datasets.find(
        (d) => d.label === realtimeLabel
      );

      // Prepare new points
      const realtimePoints = (area.realTime || []).map((item) => ({
        id: item.id,
        x: item.speed,
        y: item.flow,
        r: 4.5,
      }));

      const typicalX = Number.isFinite(area?.typical?.speed)
        ? area.typical.speed
        : null;
      const typicalY = Number.isFinite(area?.typical?.flow)
        ? area.typical.flow
        : null;
      const typicalPoints =
        typicalX != null && typicalY != null
          ? [{ x: typicalX, y: typicalY, r: 6 }]
          : [];

      if (dsRealtime) dsRealtime.data = realtimePoints;
      if (dsTypical) dsTypical.data = typicalPoints;

      // Keep axes uniform (if your backend returns maxSpeed/maxFlow)
      if (Number.isFinite(fresh?.maxSpeed)) {
        chart.options.scales.x.max = fresh.maxSpeed;
      }
      if (Number.isFinite(fresh?.maxFlow)) {
        chart.options.scales.y.max = fresh.maxFlow;
      }

      // Update without animation/flicker
      chart.update("none");
    });
  }, [realtimeLabel, t]);

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      // console.log("Fetching MFD data...");
      const fresh = await speedFlowsMFDData();

      if (!fresh) {
        console.warn("⚠️ Failed to fetch MFD data");
        if (loading) setLoading(false);
        return;
      }

      if (loading || !dataMFD) {
        // FIRST LOAD: render charts once
        setDataMFD(fresh);
        setLoading(false);
        // console.log("✅ MFD data loaded at:", new Date().toLocaleTimeString());
      } else {
        // SUBSEQUENT UPDATES: mutate charts in place (no rerender)
        applyUpdateToCharts(fresh);
        // console.log("✅ MFD data updated at:", new Date().toLocaleTimeString());
      }
    };

    // Initial fetch
    fetchData();

    // 1-minute polling
    intervalId = setInterval(fetchData, timePooling);

    return () => clearInterval(intervalId);
  }, [loading, dataMFD, applyUpdateToCharts]);

  if (loading || !dataMFD) {
    return (
      <DashboardLayout>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <MDTypography>{t("trafficMonitoring.mfd.loading")}</MDTypography>
          </MDBox>
        </Card>
      </DashboardLayout>
    );
  }

  if (!dataMFD?.areas?.length) {
    return (
      <DashboardLayout>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <MDTypography variant="h5" >
              MFD
            </MDTypography>
            <MDTypography>{t("trafficMonitoring.mfd.noDataAvailable")}</MDTypography>
          </MDBox>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Grid container spacing={2} sx={{ marginTop: "0.5rem" }}>
        {dataMFD.areas.map((area) => {
          if (!area.realTime || area.realTime.length === 0) return null;

          // Build initial chart data ONCE; later updates happen via refs
          const chartData = {
            datasets: [
              {
                label: t("trafficMonitoring.mfd.flowForArea", { areaId: area.area_id }),
                data: area.realTime.map((item) => ({
                  id: item.id,
                  x: item.speed,
                  y: item.flow,
                  r: 4.5,
                })),
                backgroundColor: "rgba(0, 117, 31, 0.8)",
                order: 1,
              },
              {
                // keep your label as is, since your plugin looks for it
                label: realtimeLabel,
                data: [
                  {
                    x:
                      Number.isFinite(area?.typical?.speed) &&
                      area.typical.speed,
                    y:
                      Number.isFinite(area?.typical?.flow) && area.typical.flow,
                    r: 6,
                  },
                ].filter((p) => p.x && p.y),
                backgroundColor: "#ffffff",
                borderColor: "#000000",
                borderWidth: 1,
                order: 999, // still good practice
              },
            ],
          };

          // Plain object (NO hooks in loops)
          const chartOptions = {
            responsive: true,
            animation: { duration: 0 },
            plugins: {
              legend: { display: true },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    const idValue = context.raw?.id;
                    const xValue = context.raw?.x;
                    const yValue = context.raw?.y;

                    if (context.dataset.label === realtimeLabel) {
                      return t("trafficMonitoring.mfd.tooltipRealtime", {
                        flow: yValue?.toFixed?.(1),
                        speed: xValue?.toFixed?.(1),
                      });
                    }
                    return idValue
                      ? t("trafficMonitoring.mfd.tooltipWithId", { id: idValue, flow: yValue, speed: xValue })
                      : t("trafficMonitoring.mfd.tooltip", { flow: yValue, speed: xValue });
                  },
                },
              },
              datalabels: { display: false },
            },
            scales: {
              x: {
                title: { display: true, text: t("trafficMonitoring.mfd.axisSpeed") },
                beginAtZero: true,
                max: dataMFD?.maxSpeed,
              },
              y: {
                title: { display: true, text: t("trafficMonitoring.mfd.axisFlow") },
                beginAtZero: true,
                max: dataMFD?.maxFlow,
              },
            },
          };

          return (
            <Grid item xs={12} md={6} lg={4} key={area.area_id}>
              <Card sx={{ padding: "1rem" }}>
                <MDBox sx={{ padding: "0.5rem" }}>
                  <MDTypography variant="h6" textAlign="center">
                    {area.name}
                  </MDTypography>
                  <Bubble
                    ref={(chartInstance) => {
                      if (chartInstance) {
                        chartRefs.current[area.area_id] = chartInstance;
                      }
                    }}
                    data={chartData}
                    options={chartOptions}
                    plugins={[realtimeOnTop]}
                  />
                </MDBox>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </DashboardLayout>
  );
}

export default MFD;
